import { Container, Stream } from '@cgs/wge';
import {
  BuyFeatureRequestParams,
  DataAccessService,
  FreeSpinsInfoEvent,
  GambleRequestParams,
  SlotMachineInfo,
  SpinInfoResponse,
  SpinResponse,
  User,
} from '@cgs/data-access';
import { IAuthorizationHolder, T_IAuthorizationHolder } from '@cgs/common';
import { ApplicationUserConfig } from '@cgs/shared';
import { MachineStateDto } from '@cgs/provider-cgs';
import { TrifectaResponseBuilder } from '../../../services/trifecta-response.builder';
import {
  isTrifectaAckSpinResultResponse,
  isTrifectaSpinResultResponse,
  TRIFECTA_LAUNCH_PARAMS_TOKEN,
  TrifectaLaunchParamsDto,
  TrifectaAckSpinResultResponse,
  TrifectaGambleResultResponse,
  TrifectaSpinResultResponse,
} from '../../../models/trifecta-dto';
import { TRIFECTA_SDK_ADAPTER_TOKEN, TrifectaSDKAdapter } from '../../../sdk/trifecta-sdk.adapter';
import { TrifectaRequestAbortedError } from './errors/trifecta-request-aborted.error';

export class TrifectaMainDataAccessService extends DataAccessService {
  protected readonly launchParams: TrifectaLaunchParamsDto;
  protected readonly responseBuilder: TrifectaResponseBuilder;

  private readonly authorizationHolder: IAuthorizationHolder;
  private readonly trifectaSDKAdapter: TrifectaSDKAdapter;

  private readonly playResponse: Stream<
    TrifectaSpinResultResponse | TrifectaAckSpinResultResponse
  > = new Stream();
  private readonly gambleResponse: Stream<TrifectaGambleResultResponse> = new Stream();

  protected readonly abortStream: Stream<void> = new Stream<void>();

  private prevBalance: number = 0;

  private recoverySpins: MachineStateDto[] = [];

  constructor(container: Container) {
    super();

    this.launchParams = container.resolve<TrifectaLaunchParamsDto>(TRIFECTA_LAUNCH_PARAMS_TOKEN);
    this.responseBuilder = new TrifectaResponseBuilder(this.launchParams);
    this.authorizationHolder = container.resolve<IAuthorizationHolder>(T_IAuthorizationHolder);
    this.trifectaSDKAdapter = container.resolve<TrifectaSDKAdapter>(TRIFECTA_SDK_ADAPTER_TOKEN);
  }

  public override async getUserInfo(): Promise<User> {
    const userInfo = new User({
      userId: this.authorizationHolder.userId,
      cheatUser: ApplicationUserConfig.cheatUser,
      balance: this.launchParams.balance,
    });
    return Promise.resolve(userInfo);
  }

  public override async start(): Promise<SlotMachineInfo> {
    let lastSpin = this.launchParams.resumeData?.spins[0];

    // Это костыль, когда у нас рекавери мод, и остается последний фриспин.
    // Чтобы его отыграть, начальное состояние мы феково генерим
    // И записываем в recoverySpins этот спин, чтобы не делать запросы лишние
    if (lastSpin?.freeSpinsInfo?.event === FreeSpinsInfoEvent.FreeSpinsFinished) {
      this.recoverySpins = [lastSpin];

      lastSpin = {
        ...lastSpin,
        freeSpinsInfo: {
          ...lastSpin.freeSpinsInfo,
          currentFreeSpinsGroup: { ...lastSpin.freeSpinsInfo.currentFreeSpinsGroup },
          freeSpinGroups: lastSpin.freeSpinsInfo.freeSpinGroups.map((group) => ({ ...group })),
        },
      };
      lastSpin.freeSpinsInfo!.event = '';
      lastSpin.freeSpinsInfo!.totalFreeSpins -= 1;
      lastSpin.freeSpinsInfo!.currentFreeSpinsGroup.count += 1;
      lastSpin.freeSpinsInfo!.currentFreeSpinsGroup.usedCount -= 1;
      const lastGroup =
        lastSpin.freeSpinsInfo!.freeSpinGroups[lastSpin.freeSpinsInfo!.freeSpinGroups.length - 1];
      lastGroup.count += 1;
      lastGroup.usedCount -= 1;
    }

    this.prevBalance = this.launchParams.balance;

    return this.responseBuilder.buildMachineInfoSDK(lastSpin);
  }

  public override async spin(prevSpinResponse: SpinResponse | null): Promise<SpinInfoResponse> {
    if (this.recoverySpins.length) {
      const recoverySpin = this.recoverySpins.shift()!;
      return this.responseBuilder.buildSpinResultResponse({
        balance: this.prevBalance,
        spin: recoverySpin,
        sequence: 0,
      }).response;
    }

    if (prevSpinResponse?.isFreeSpinsActive) {
      return this.ackSpinInfoResponse();
    }

    const abortPromise = this.abortStream.toPromise().then(() => {
      throw new TrifectaRequestAbortedError();
    });
    const response = await Promise.race([this.playResponse.toPromise(), abortPromise]);

    const isSpinResponse = isTrifectaSpinResultResponse(response);

    this.prevBalance = isSpinResponse ? response.balance : this.prevBalance;

    const buildData = isSpinResponse
      ? response
      : {
          balance: this.prevBalance,
          spin: response.spin,
          sequence: 0,
        };

    const spinResponse = this.responseBuilder.buildSpinResultResponse(buildData);
    return spinResponse.response;
  }

  public override async buyFeature(_params: BuyFeatureRequestParams): Promise<SpinInfoResponse> {
    const abortPromise = this.abortStream.toPromise().then(() => {
      throw new TrifectaRequestAbortedError();
    });
    const response = await Promise.race([this.playResponse.toPromise(), abortPromise]);
    const isSpinResponse = isTrifectaSpinResultResponse(response);
    this.prevBalance = isSpinResponse ? response.balance : this.prevBalance;
    const buildData = isSpinResponse
      ? response
      : { balance: this.prevBalance, spin: response.spin, sequence: 0 };
    return this.responseBuilder.buildSpinResultResponse(buildData).response;
  }

  public abortCurrentRequest(): void {
    this.abortStream.emit();
  }

  public override async pickBonus(_params: any): Promise<SpinInfoResponse> {
    return this.ackSpinInfoResponse();
  }

  public override async gamble<Data = any>(
    _params: GambleRequestParams<Data>
  ): Promise<SpinInfoResponse> {
    const response = await this.gambleResponse.toPromise();
    this.prevBalance = response.balance;
    const buildData = { ...response, spin: response.spin.spin };
    return this.responseBuilder.buildSpinResultResponse(buildData).response;
  }

  protected async ackSpinInfoResponse(): Promise<SpinInfoResponse> {
    const response = await this.trifectaSDKAdapter.acknowledgeSpins();

    if (!isTrifectaAckSpinResultResponse(response)) {
      throw new Error('Invalid spin response');
    }

    const spinResponse = this.responseBuilder.buildSpinResultResponse({
      ...response,
      balance: this.prevBalance,
      sequence: 0,
    });
    return spinResponse.response;
  }

  public nextPlayResponse(
    response: TrifectaSpinResultResponse | TrifectaAckSpinResultResponse
  ): void {
    this.playResponse.emit(response);
  }

  public nextGambleResponse(response: TrifectaGambleResultResponse): void {
    this.gambleResponse.emit(response);
  }
}
